import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

type UseHasMaxHeightParams = {
	sidebarEl: React.MutableRefObject<HTMLElement | null>;
	isRefSet: boolean;
	isSticky: boolean;
	isFabricPage?: boolean;
	annotationElement?: HTMLElement | null;
	isEditor?: boolean;
	sidebarOffset?: number;
	isMaxHeightEnabledForSticky?: boolean;
};

type UseHasMaxHeightReturn = {
	hasMaxHeight: boolean;
	dynamicMaxHeight?: string;
};

export const useHasMaxHeight = ({
	sidebarEl,
	isRefSet,
	isSticky,
	isFabricPage,
	annotationElement,
	isEditor,
	sidebarOffset,
	isMaxHeightEnabledForSticky,
}: UseHasMaxHeightParams): UseHasMaxHeightReturn => {
	const [hasMaxHeight, setHasMaxHeight] = useState(false);
	const [dynamicMaxHeight, setDynamicMaxHeight] = useState<string | undefined>(undefined);

	// Shared max height check logic
	const checkMaxHeight = useCallback(() => {
		// Check if annotation is in a sticky table header & on edit mode
		if (
			annotationElement &&
			isEditor &&
			isMaxHeightEnabledForSticky &&
			expValEquals('cc_comments_dynamic_inline_height', 'isEnabled', true)
		) {
			const stickyTable = annotationElement.closest('table.pm-table-sticky');
			const inStickyTableHeader = Boolean(stickyTable);

			// Special case: annotation in sticky table header - calculate max height based on where sidebar will be positioned
			// Without this check, long comment threads can sometimes have their bottom permanently below the viewport on small-width pages
			if (inStickyTableHeader && isSticky) {
				const annotationRect = annotationElement.getBoundingClientRect();
				const annotationBottom = annotationRect.bottom;
				// Use the sidebar offset that will be applied (STICKY_COMMENT_VERTICAL_OFFSET_VIEW for view, or sidebarOffset for editor)
				const offset = sidebarOffset ?? 0;
				const sidebarTop = annotationBottom + offset;
				const viewportHeight = window.innerHeight;
				// Calculate max height as distance from where sidebar top will be to bottom of viewport
				const calculatedMaxHeight = viewportHeight - sidebarTop;
				// Only apply if the calculated max height is positive and would actually constrain the sidebar
				// Otherwise, continue to default behavior
				if (calculatedMaxHeight > 0) {
					setDynamicMaxHeight(`${calculatedMaxHeight}px`);
					setHasMaxHeight(true);
					return;
				}
			}
		}

		// Default (i.e. non-special-cate) behavior
		if (!sidebarEl.current) return;

		if (isSticky) {
			setHasMaxHeight(true);
			setDynamicMaxHeight(undefined);
			return;
		}

		const rect = sidebarEl.current.getBoundingClientRect();
		const availableHeight = window.innerHeight - 300;

		setHasMaxHeight(rect.height > availableHeight);
		setDynamicMaxHeight(undefined);
	}, [
		isSticky,
		sidebarEl,
		annotationElement,
		isEditor,
		sidebarOffset,
		isMaxHeightEnabledForSticky,
	]);

	// Max height effect for pre-render (useLayoutEffect)
	useLayoutEffect(() => {
		if (!fg('confluence_frontend_content_wrapper')) {
			return;
		}
		if (!sidebarEl.current) return;

		if (!expValEquals('confluence_compute_sidebar_styles_pre-render', 'isEnabled', true)) {
			return;
		}

		checkMaxHeight();
	}, [checkMaxHeight, isRefSet, isFabricPage, sidebarEl]);

	// Max height effect for post-render (useEffect)
	useEffect(() => {
		if (!fg('confluence_frontend_content_wrapper')) {
			return;
		}
		if (!sidebarEl.current) return;

		if (expValEquals('confluence_compute_sidebar_styles_pre-render', 'isEnabled', true)) {
			return;
		}

		checkMaxHeight();
	}, [checkMaxHeight, isRefSet, isFabricPage, sidebarEl]);

	return { hasMaxHeight, dynamicMaxHeight };
};
